import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Cartão de indicador.
 *
 * O hover não é enfeite: o cartão é CLICÁVEL quando recebe `aoClicar` — é como
 * se filtra a lista por "em aberto" sem abrir o painel de filtros. Sem a
 * reação ao passar o mouse, ninguém descobre que dá para clicar, e o atalho
 * existe para quem nunca vai encontrá-lo.
 *
 * Quando não há clique, o cartão ainda reage — de leve. É o que diferencia um
 * número vivo de um adesivo, e custa uma sombra.
 */
export function CartaoKpi({
  rotulo,
  valor,
  detalhe,
  icone: Icone,
  tom = 'neutro',
  aoClicar,
  ativo,
  children,
}: {
  rotulo: string;
  valor: string;
  /** Uma linha abaixo do número: "no período", "por pedido". */
  detalhe?: string;
  icone?: LucideIcon;
  tom?: 'neutro' | 'ok' | 'alerta' | 'erro' | 'marca';
  aoClicar?: () => void;
  ativo?: boolean;
  /** Linhas de apoio — barras de proporção, quebras por situação. */
  children?: ReactNode;
}) {
  const cor = {
    neutro: 'bg-tinta-100 text-tinta-700',
    ok: 'bg-ok-100 text-ok-600',
    alerta: 'bg-alerta-100 text-alerta-600',
    erro: 'bg-erro-100 text-erro-600',
    marca: 'bg-marca-100 text-marca-600',
  }[tom];

  const conteudo = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/*
            O rótulo CORTA, e não quebra como o `detalhe` abaixo.

            Tentei quebrar e é pior: numa grade de duas colunas a 375px,
            "Administradores" é uma palavra só — sem onde quebrar, ela vaza por
            trás do ícone em vez de reticenciar. O rótulo de um indicador é
            curto POR CONTRATO; quando não couber, encurte o rótulo e ponha a
            explicação no `detalhe`, que tem a linha inteira.
          */}
          <p className="text-tinta-500 truncate text-sm">{rotulo}</p>
          <p className="text-tinta-900 truncate pt-1 text-2xl font-semibold tabular-nums">
            {valor}
          </p>
          {/*
            QUEBRA no celular, corta a partir de `sm`. A grade de indicadores é
            de duas colunas mesmo em 375px: sobram ~11 caracteres a 12px, e
            "sócios e administradores" virava "sócios e adm…". Cortar um número
            é impensável; cortar a linha que EXPLICA o número é a mesma perda,
            só que silenciosa. No desktop há largura, e ali o corte volta a
            valer para o detalhe muito longo.
          */}
          {detalhe && <p className="text-tinta-500 text-xs sm:truncate">{detalhe}</p>}
        </div>
        {Icone && (
          <span
            className={`inline-flex size-9 shrink-0 items-center justify-center rounded-full ${cor}`}
          >
            <Icone className="size-4" aria-hidden />
          </span>
        )}
      </div>
      {children && (
        <div className="border-tinta-100 mt-3 space-y-1 border-t pt-2">{children}</div>
      )}
    </>
  );

  const base = `border-tinta-100 bg-superficie rounded-xl border p-4 text-left transition-all duration-150 ${
    ativo ? 'border-marca-400 ring-marca-400/40 ring-2' : ''
  }`;

  if (!aoClicar) {
    return <div className={`${base} hover:shadow-md`}>{conteudo}</div>;
  }

  return (
    <button
      type="button"
      onClick={aoClicar}
      aria-pressed={ativo === true}
      className={`${base} hover:border-marca-400 w-full hover:-translate-y-0.5 hover:shadow-lg`}
    >
      {conteudo}
    </button>
  );
}

/**
 * Uma linha de apoio dentro do cartão, com barra de proporção.
 *
 * A barra responde "quanto disso é isso" sem obrigar ninguém a dividir dois
 * números de cabeça — que é exatamente o que se faz ao olhar "50 em aberto" e
 * "106 faturados" lado a lado.
 */
export function LinhaKpi({
  rotulo,
  valor,
  proporcao,
  tom = 'marca',
}: {
  rotulo: string;
  valor: string;
  /** 0 a 1. Sem isto, a linha é só texto. */
  proporcao?: number;
  tom?: 'marca' | 'ok' | 'alerta' | 'erro';
}) {
  const cor = {
    marca: 'bg-marca-600',
    ok: 'bg-ok-600',
    alerta: 'bg-alerta-600',
    erro: 'bg-erro-600',
  }[tom];

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-tinta-500 min-w-0 flex-1 truncate">{rotulo}</span>
      {proporcao !== undefined && (
        <span className="bg-tinta-100 hidden h-1.5 w-20 overflow-hidden rounded-full sm:block">
          <span
            className={`block h-full rounded-full ${cor}`}
            style={{ width: `${Math.max(2, Math.min(100, proporcao * 100))}%` }}
          />
        </span>
      )}
      <span className="text-tinta-900 shrink-0 font-medium tabular-nums">{valor}</span>
    </div>
  );
}
